/**
 * POS Sales Control Page
 *
 * Main page for controlling and auditing POS sales imports.
 */

import { useCallback } from 'react';
import { PageHeader, formatDateParam } from '@/components/ui';
import { useDebounce, useUrlParams, useBrandContext, usePermission, useCanSeeFinancials } from '@/hooks';
import {
  PosSalesKPIs,
  PosSalesFilters,
  PosSalesSyncBar,
  ReceiptsTable,
  BatchesTable,
  ReceiptDetailDrawer,
  PosProductsInspectionTab,
  RawProductsInspectionTab,
  SyncProgress,
  SalesCompletenessNotice,
} from '../components';
import type { HasMovementsFilter } from '../components';
import type { SortField as ReceiptsSortField, SortOrder as ReceiptsSortOrder } from '../components/ReceiptsTable';
import type { SortField as BatchesSortField, SortOrder as BatchesSortOrder } from '../components/BatchesTable';
import type { ProductSaleEventsSortField } from '@bstock/shared';
import {
  usePosReceipts,
  useImportBatches,
  useSyncSalesWithProgress,
  useSyncStatus,
} from '../hooks';
import { useGeneratePOSMovements } from '@/features/stock-movements';
import type { SaleType, CancelStatus } from '@bstock/shared';

type TabType = 'receipts' | 'batches' | 'pos-products' | 'raw-products';

const DEFAULT_LIMIT = 20;

export default function PosSalesControlPage() {
  const { isSite } = useBrandContext();
  const canSync = usePermission('pos-sales.sync');
  const showFinancials = useCanSeeFinancials();

  // Get today's date in YYYY-MM-DD format (local timezone)
  const today = formatDateParam(new Date()) ?? '';

  // URL-synced filters with sort, limit and tab (deep linking)
  const { params, setParam, setParams, resetParams } = useUrlParams({
    tab: { default: 'receipts' as TabType },
    startDate: { default: today },
    endDate: { default: today },
    cancelStatus: { default: 'all' as CancelStatus },
    search: { default: '' },
    saleTypes: { default: '' },
    page: { default: 1, type: 'number' },
    limit: { default: DEFAULT_LIMIT, type: 'number' },
    // Receipts sort (used when activeTab = 'receipts')
    sortBy: { default: 'createdAt' as ReceiptsSortField },
    sortOrder: { default: 'desc' as ReceiptsSortOrder },
    // Batches pagination (separate page for batches tab)
    batchesPage: { default: 1, type: 'number' },
    batchesSortBy: { default: 'createdAt' as BatchesSortField },
    batchesSortOrder: { default: 'desc' as BatchesSortOrder },
    // Drawer state (deep linking for receipt detail)
    receiptId: { default: '' },
    // Product filters (deep linked for inspection tabs)
    posProductId: { default: '' },
    rawProductId: { default: '' },
    // Raw products sort (used when activeTab = 'raw-products')
    rawSortBy: { default: 'receiptDate' as ProductSaleEventsSortField },
    rawSortOrder: { default: 'desc' as 'asc' | 'desc' },
    // Movements filter (US-4 Sprint 21)
    hasMovements: { default: '' as HasMovementsFilter },
  });

  // Debounce search
  const debouncedSearch = useDebounce(params.search, 300);

  // Parse sale types from URL
  const saleTypes: SaleType[] = params.saleTypes
    ? (params.saleTypes.split(',').filter(Boolean) as SaleType[])
    : [];

  // Sync status and SSE-based sync with progress
  const { data: syncStatus } = useSyncStatus();
  const {
    startSync,
    cancelSync,
    progress: syncProgress,
    error: syncError,
    isLoading: isSyncing,
  } = useSyncSalesWithProgress();

  // Generate POS movements mutation
  const generateMovements = useGeneratePOSMovements();

  // Data fetching for receipts
  const receiptsQuery = usePosReceipts({
    page: params.page,
    limit: params.limit,
    startDate: params.startDate,
    endDate: params.endDate,
    cancelStatus: params.cancelStatus as CancelStatus,
    search: debouncedSearch || undefined,
    saleTypes: saleTypes.length > 0 ? saleTypes : undefined,
    sortBy: params.sortBy as ReceiptsSortField,
    sortOrder: params.sortOrder as ReceiptsSortOrder,
    // US-4: Pass hasMovements filter
    hasMovements: params.hasMovements === 'true' ? true
                : params.hasMovements === 'false' ? false
                : undefined,
  });

  // Data fetching for batches
  const batchesQuery = useImportBatches({
    page: params.batchesPage,
    limit: params.limit,
    sortBy: params.batchesSortBy as BatchesSortField,
    sortOrder: params.batchesSortOrder as BatchesSortOrder,
  });

  // Check if filters are active
  const hasActiveFilters = Boolean(
    params.search ||
    params.cancelStatus !== 'all' ||
    saleTypes.length > 0 ||
    params.startDate !== today ||
    params.endDate !== today ||
    params.posProductId ||
    params.rawProductId ||
    params.hasMovements // US-4: Include hasMovements in active filters check
  );

  // Handlers
  const handleDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      setParams({ startDate, endDate, page: 1 });
    },
    [setParams]
  );

  const handleCancelStatusChange = useCallback(
    (status: CancelStatus) => {
      setParams({ cancelStatus: status, page: 1 });
    },
    [setParams]
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      setParams({ search, page: 1 });
    },
    [setParams]
  );

  const handleSaleTypesChange = useCallback(
    (types: SaleType[]) => {
      setParams({ saleTypes: types.join(','), page: 1 });
    },
    [setParams]
  );

  const handlePosProductIdChange = useCallback(
    (id: string) => {
      setParam('posProductId', id);
    },
    [setParam]
  );

  const handleRawProductIdChange = useCallback(
    (id: string) => {
      setParam('rawProductId', id);
    },
    [setParam]
  );

  // US-4: Handler for hasMovements filter change
  const handleHasMovementsChange = useCallback(
    (value: HasMovementsFilter) => {
      setParams({ hasMovements: value, page: 1 });
    },
    [setParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setParam('page', page);
    },
    [setParam]
  );

  const handleLimitChange = useCallback(
    (limit: number) => {
      setParams({ limit, page: 1, batchesPage: 1 }); // Reset both pages
    },
    [setParams]
  );

  const handleReceiptsSort = useCallback(
    (field: ReceiptsSortField, order: ReceiptsSortOrder) => {
      setParams({ sortBy: field, sortOrder: order, page: 1 }); // Reset page on sort
    },
    [setParams]
  );

  const handleBatchesPageChange = useCallback(
    (page: number) => {
      setParam('batchesPage', page);
    },
    [setParam]
  );

  const handleBatchesSort = useCallback(
    (field: BatchesSortField, order: BatchesSortOrder) => {
      setParams({ batchesSortBy: field, batchesSortOrder: order, batchesPage: 1 }); // Reset page on sort
    },
    [setParams]
  );

  const handleRawProductsSort = useCallback(
    (field: ProductSaleEventsSortField, order: 'asc' | 'desc') => {
      setParams({ rawSortBy: field, rawSortOrder: order });
    },
    [setParams]
  );

  const handleReset = useCallback(() => {
    resetParams();
  }, [resetParams]);

  const handleSync = useCallback(() => {
    startSync();
  }, [startSync]);

  const handleResync = useCallback(
    (range: { from: string; to: string }) => {
      startSync(range);
    },
    [startSync]
  );

  const handleCancelSync = useCallback(() => {
    cancelSync();
  }, [cancelSync]);

  const handleGenerateMovements = useCallback(() => {
    // Generate movements for the current date range filter
    generateMovements.mutate({
      startDate: params.startDate,
      endDate: params.endDate,
    });
  }, [generateMovements, params.startDate, params.endDate]);

  const handleReceiptClick = useCallback((receiptId: string) => {
    setParam('receiptId', receiptId);
  }, [setParam]);

  const handleBatchClick = useCallback(
    (_batchId: string) => {
      // Switch to receipts tab and filter by batch
      // For simplicity, we'll just switch tabs
      // In a full implementation, we'd add a batchId filter
      setParam('tab', 'receipts');
    },
    [setParam]
  );

  const handleDrawerClose = useCallback((open: boolean) => {
    if (!open) {
      setParam('receiptId', '');
    }
  }, [setParam]);

  // Show sync button only for site context (child brand), not for group (parent brand)
  // Also check permission
  const showSync = isSite && canSync && (syncStatus?.billApiConfigured ?? false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Controle Ventes POS"
        subtitle="Console d'inspection et de verification des imports de ventes"
      />

      {/* Sync Bar */}
      <PosSalesSyncBar
        syncStatus={syncStatus}
        isLoading={!syncStatus}
        onSync={handleSync}
        onResync={handleResync}
        isSyncing={isSyncing}
        showSync={showSync}
        onGenerateMovements={handleGenerateMovements}
        isGeneratingMovements={generateMovements.isPending}
        showGenerateMovements={isSite}
      />

      {/* Completude des ventes : tickets en attente de la caisse */}
      <SalesCompletenessNotice mode="control" />

      {/* Filters */}
      <PosSalesFilters
        startDate={params.startDate}
        endDate={params.endDate}
        onDateRangeChange={handleDateRangeChange}
        cancelStatus={params.cancelStatus as CancelStatus}
        onCancelStatusChange={handleCancelStatusChange}
        search={params.search}
        onSearchChange={handleSearchChange}
        saleTypes={saleTypes}
        onSaleTypesChange={handleSaleTypesChange}
        posProductId={params.posProductId}
        onPosProductIdChange={handlePosProductIdChange}
        rawProductId={params.rawProductId}
        onRawProductIdChange={handleRawProductIdChange}
        onReset={handleReset}
        hasActiveFilters={hasActiveFilters}
        showSaleTypes={params.tab === 'pos-products'}
        hasMovements={params.hasMovements as HasMovementsFilter}
        onHasMovementsChange={handleHasMovementsChange}
        showMovementsFilter={params.tab === 'receipts'}
      />

      {/* Sync Progress (shown when syncing or error) */}
      {(isSyncing || syncError) && (
        <div className="space-y-2">
          <SyncProgress progress={syncProgress} error={syncError} />
          {isSyncing && (
            <button
              onClick={handleCancelSync}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Annuler la synchronisation
            </button>
          )}
        </div>
      )}

      {/* KPIs */}
      <PosSalesKPIs
        kpis={receiptsQuery.data?.kpis}
        isLoading={receiptsQuery.isLoading}
        showFinancials={showFinancials}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setParam('tab', 'receipts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              params.tab === 'receipts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tickets POS
            {receiptsQuery.data && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100">
                {receiptsQuery.data.pagination.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setParam('tab', 'batches')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              params.tab === 'batches'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Import Batches
            {batchesQuery.data && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100">
                {batchesQuery.data.pagination.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setParam('tab', 'pos-products')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              params.tab === 'pos-products'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Produits Finis
          </button>
          <button
            onClick={() => setParam('tab', 'raw-products')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              params.tab === 'raw-products'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Produits Bruts
          </button>
        </nav>
      </div>

      {/* Tab content */}
      {params.tab === 'receipts' && (
        <ReceiptsTable
          receipts={receiptsQuery.data?.items ?? []}
          pagination={
            receiptsQuery.data?.pagination ?? {
              page: 1,
              limit: params.limit,
              total: 0,
              totalPages: 0,
            }
          }
          isLoading={receiptsQuery.isLoading}
          onRowClick={handleReceiptClick}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          sortBy={params.sortBy as ReceiptsSortField}
          sortOrder={params.sortOrder as ReceiptsSortOrder}
          onSort={handleReceiptsSort}
          showFinancials={showFinancials}
        />
      )}

      {params.tab === 'batches' && (
        <BatchesTable
          batches={batchesQuery.data?.items ?? []}
          pagination={
            batchesQuery.data?.pagination ?? {
              page: 1,
              limit: params.limit,
              total: 0,
              totalPages: 0,
            }
          }
          isLoading={batchesQuery.isLoading}
          onRowClick={handleBatchClick}
          onPageChange={handleBatchesPageChange}
          onLimitChange={handleLimitChange}
          sortBy={params.batchesSortBy as BatchesSortField}
          sortOrder={params.batchesSortOrder as BatchesSortOrder}
          onSort={handleBatchesSort}
        />
      )}

      {params.tab === 'pos-products' && (
        <PosProductsInspectionTab
          startDate={params.startDate}
          endDate={params.endDate}
          posProductId={params.posProductId}
          saleTypes={saleTypes.length > 0 ? saleTypes : undefined}
          search={debouncedSearch || undefined}
          onReceiptClick={handleReceiptClick}
          showFinancials={showFinancials}
        />
      )}

      {params.tab === 'raw-products' && (
        <RawProductsInspectionTab
          startDate={params.startDate}
          endDate={params.endDate}
          rawProductId={params.rawProductId}
          posProductId={params.posProductId}
          search={debouncedSearch || undefined}
          onReceiptClick={handleReceiptClick}
          showFinancials={showFinancials}
          sortBy={params.rawSortBy as ProductSaleEventsSortField}
          sortOrder={params.rawSortOrder as 'asc' | 'desc'}
          onSort={handleRawProductsSort}
        />
      )}

      {/* Drawer (deep linked via URL param receiptId) */}
      <ReceiptDetailDrawer
        receiptId={params.receiptId || null}
        open={!!params.receiptId}
        onOpenChange={handleDrawerClose}
        showFinancials={showFinancials}
      />
    </div>
  );
}
